/**
 * BundleActions — Copy-to-clipboard and download license bundle buttons.
 */

import { useState, useCallback } from 'react';
import { Copy, Download, CheckCircle, Loader2, AlertTriangle, MousePointer2 } from 'lucide-react';
import { trackActivationEvent } from '../lib/analytics';

const EXTERNAL_ACTIVATION_MESSAGE = 'HFORGEX_ACTIVATE_LICENSE_BUNDLE';

function sendBundleToExtension(extensionId, bundleText) {
    const runtime = window.chrome?.runtime;
    if (!runtime?.sendMessage || !extensionId) {
        return Promise.resolve({
            success: false,
            error: 'EXTENSION_UNAVAILABLE',
            message: 'This extension is not reachable from this browser.',
        });
    }

    return new Promise((resolve) => {
        runtime.sendMessage(
            extensionId,
            { type: EXTERNAL_ACTIVATION_MESSAGE, bundleText },
            (response) => {
                const lastError = runtime.lastError;
                if (lastError) {
                    resolve({
                        success: false,
                        error: 'EXTENSION_UNAVAILABLE',
                        message: lastError.message,
                    });
                    return;
                }
                resolve(response || { success: false, error: 'NO_RESPONSE' });
            }
        );
    });
}

export function BundleActions({
    onIssueBundle,
    hasDevices,
    isRevoked,
    activationTarget = null,
    onBeforeIssueBundle = null,
    trackingContext = null,
}) {
    const [status, setStatus] = useState('idle');
    const [errorMsg, setErrorMsg] = useState(null);

    const canPrepareDevice = Boolean(onBeforeIssueBundle);
    const disabled = (!hasDevices && !canPrepareDevice) || isRevoked;
    const canActivateInExtension = Boolean(activationTarget?.extensionId);

    const doIssue = useCallback(async () => {
        setStatus('loading');
        setErrorMsg(null);
        try {
            if (onBeforeIssueBundle) {
                await onBeforeIssueBundle();
            }
            const data = await onIssueBundle();
            if (!data?.bundleText) {
                throw new Error('Failed to generate license bundle.');
            }
            return data;
        } catch (err) {
            setStatus('error');
            setErrorMsg(err.message || 'Failed to generate license bundle.');
            return null;
        }
    }, [onBeforeIssueBundle, onIssueBundle]);

    async function handleCopy() {
        const data = await doIssue();
        if (!data) return;
        try {
            await navigator.clipboard.writeText(data.bundleText);
            trackActivationEvent('copy_bundle', trackingContext || {});
            setStatus('copied');
            setTimeout(() => setStatus('idle'), 3000);
        } catch {
            setStatus('error');
            setErrorMsg('Clipboard access denied. Try downloading instead.');
        }
    }

    async function handleDownload() {
        const data = await doIssue();
        if (!data) return;
        const blob = new Blob([data.bundleText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'hforgex-license.txt';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        trackActivationEvent('download_bundle', trackingContext || {});
        setStatus('downloaded');
        setTimeout(() => setStatus('idle'), 3000);
    }

    async function handleActivateInExtension() {
        const data = await doIssue();
        if (!data?.bundleText) return;

        const result = await sendBundleToExtension(activationTarget.extensionId, data.bundleText);
        if (result?.success) {
            trackActivationEvent('activate_in_extension', trackingContext || {});
            setStatus('activated');
            setTimeout(() => setStatus('idle'), 4000);
            return;
        }

        setStatus('error');
        setErrorMsg(
            result?.message ||
                'Could not activate this extension automatically. Copy the activation code instead.'
        );
    }

    const feedbackIcon =
        status === 'copied' || status === 'downloaded' || status === 'activated' ? (
            <CheckCircle className="w-4 h-4 text-emerald-500" />
        ) : null;

    return (
        <section className="space-y-3">
            <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                Activation Code
            </h3>

            {disabled && !isRevoked && (
                <div className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-lg">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Add at least one device before creating your activation code.</span>
                </div>
            )}

            {isRevoked && (
                <div className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-lg">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>This license has been revoked. Contact support for assistance.</span>
                </div>
            )}

            <div className="flex gap-3">
                {canActivateInExtension && (
                    <button
                        type="button"
                        onClick={handleActivateInExtension}
                        disabled={disabled || status === 'loading'}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {status === 'loading' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : status === 'activated' ? (
                            feedbackIcon
                        ) : (
                            <MousePointer2 className="w-4 h-4" />
                        )}
                        {status === 'activated'
                            ? 'Activated!'
                            : `Activate ${activationTarget.productName || 'this extension'}`}
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleCopy}
                    disabled={disabled || status === 'loading'}
                    className={
                        canActivateInExtension
                            ? 'flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                            : 'flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                    }
                >
                    {status === 'loading' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : status === 'copied' ? (
                        feedbackIcon
                    ) : (
                        <Copy className="w-4 h-4" />
                    )}
                    {status === 'copied' ? 'Copied!' : 'Copy Activation Code'}
                </button>
                <button
                    type="button"
                    onClick={handleDownload}
                    disabled={disabled || status === 'loading'}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {status === 'downloaded' ? feedbackIcon : <Download className="w-4 h-4" />}
                    {status === 'downloaded' ? 'Done!' : 'Download'}
                </button>
            </div>

            {status === 'error' && errorMsg && (
                <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                    {errorMsg}
                </p>
            )}
        </section>
    );
}
